import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import { useResolvePilabRequest } from '../../../hooks/usePilabRequests.js';
import { ImprovementForm } from './ImprovementForm.js';
import { MoveToIssueModal } from './MoveToIssueModal.js';
import { areaLabel } from './area-options.js';
import type {
  ImprovementRequest,
  ImprovementResolution,
  ImprovementStatus,
} from '../../../lib/api/pilab-requests.js';

/** 状態に合わせたバッジの色。 @graph-connects none */
const STATUS_VARIANTS: Record<ImprovementStatus, 'default' | 'success' | 'warning' | 'info'> = {
  pending: 'default',
  needs_decision: 'warning',
  rejected: 'default',
  withdrawn: 'default',
  moved: 'default',
  in_progress: 'info',
  shipped: 'success',
};

/**
 * 出した人が自分で触れる状態。**実装が始まったら触らせない。**
 *
 * **ここは見え方の制御だけ。** 誰が触れるか・どの状態まで触れるかの判定は API の向こう側
 * （domain）にあり、押せてしまっても 403 で止まる。ボタンを出さないのは、押しても通らない
 * ものを見せないためで、権限をここで守っているのではない。
 * @graph-connects none
 */
const SELF_SERVICE_STATUSES: readonly ImprovementStatus[] = ['pending', 'needs_decision'];

/**
 * 要望 1 件。判断待ち・却下のものには判断する操作を出す。
 *
 * **AI の判断と人の判断を並べて出す。** どちらかだけ見せると、どこで判断が割れたのかが
 * 残らず、基準を直す材料にならない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 改善要望 1 件の表示と、CTO / PMO による判断（やる・やらない・判断待ちに戻す）の記録・チーム課題への移動。自分が出したものは未判断の間、取り下げと書き直しもできる。移した要望は移した先の課題へのリンクを出す
 * @graph-connects useResolvePilabRequest [calls] 人の判断・取り下げの記録
 * @graph-connects areaLabel [calls] 対象画面の表示名
 * @graph-connects ImprovementForm [calls] 要望の書き直しフォーム
 * @graph-connects MoveToIssueModal [calls] チーム課題への移動
 */
export function ImprovementCard({
  request,
  viewerEmail,
  canDecide,
  canMove = false,
}: {
  request: ImprovementRequest;
  viewerEmail: string;
  /**
   * 「この要望を実行してよいか」を判断できるか（CTO / PMO）。
   *
   * **読めることと判断できることを分ける。** 自分の要望がなぜ止まっているかは誰でも読めて
   * よく、判断だけを絞る。判定は API 側（`DECIDE_IMPROVEMENT_REQUEST`）が正で、ここは
   * 押しても通らないボタンを見せないための見え方の制御。
   */
  canDecide: boolean;
  /**
   * チーム課題に移せるか（CTO / PMO チーム）。判定は API 側（`MOVE_IMPROVEMENT_REQUEST`）が正で、
   * ここは押しても通らないボタンを見せないための見え方の制御
   */
  canMove?: boolean;
}): ReactNode {
  const { t } = useTranslation();
  const resolve = useResolvePilabRequest();
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [moving, setMoving] = useState(false);

  // 自分が出したもので、まだ実装が始まっていないなら引っ込められる
  const mine = viewerEmail !== '' && request.requestedBy === viewerEmail;
  const selfService = mine && SELF_SERVICE_STATUSES.includes(request.status);
  // 書き直せるのは判断待ちだけ（未振り分けは bot が振り分けている最中かもしれない）
  const canEdit = mine && request.status === 'needs_decision';

  // 判断待ちと却下だけ人が触れる。実装中・反映済みは bot が進めているので触らせない
  const decidable =
    canDecide && (request.status === 'needs_decision' || request.status === 'rejected');
  // 移せるのは、まだ人の判断も実装も始まっていないもの（未振り分け・判断待ち）
  const movable = canMove && SELF_SERVICE_STATUSES.includes(request.status);

  const decide = (resolution: ImprovementResolution): void => {
    resolve.mutate(
      { id: request.id, input: { resolution, note } },
      {
        onSuccess: () => {
          setNote('');
          setOpen(false);
        },
      },
    );
  };

  return (
    <Card padding="sm">
      <CardContent>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-[var(--color-text-primary)]">
              {request.title}
            </div>
            <div className="mt-0.5 text-xs text-[var(--color-text-muted)]">
              {areaLabel(request.area, t)} ·{' '}
              {t('improvements.list.requestedBy', {
                email: request.requestedBy,
                date: request.createdAt.slice(0, 10),
              })}
            </div>
          </div>
          <Badge variant={STATUS_VARIANTS[request.status]} size="sm">
            {t(`improvements.status.${request.status}`)}
          </Badge>
        </div>

        <p className="mt-2 whitespace-pre-wrap text-sm text-[var(--color-text-primary)]">
          {request.body}
        </p>
        <p className="mt-2 whitespace-pre-wrap text-xs text-[var(--color-text-muted)]">
          {t('improvements.list.reason')}: {request.reason}
        </p>

        {request.attachments.length > 0 && (
          <ul className="mt-2 flex flex-wrap gap-2">
            {request.attachments.map((url) => (
              <li key={url}>
                <a href={url} target="_blank" rel="noreferrer">
                  <img
                    src={url}
                    alt={t('improvements.list.attachment')}
                    className="h-20 w-20 rounded border object-cover"
                  />
                </a>
              </li>
            ))}
          </ul>
        )}

        {request.triageNote !== '' && (
          <p className="mt-2 text-xs text-[var(--color-text-muted)]">
            {t('improvements.list.triage')}（{request.triage}）: {request.triageNote}
          </p>
        )}
        {request.resolutionNote !== '' && (
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            {t('improvements.list.resolution')}（{request.resolution}）: {request.resolutionNote}
          </p>
        )}
        {request.prUrl !== '' && (
          <a
            className="mt-2 inline-block text-xs text-[var(--color-accent-primary)] underline"
            href={request.prUrl}
            target="_blank"
            rel="noreferrer"
          >
            {t('improvements.list.pr')}
          </a>
        )}

        {request.movedTo !== undefined && (
          <Link
            className="mt-2 inline-block text-xs text-[var(--color-accent-primary)] underline"
            to="/org-issues/$boardId"
            params={{ boardId: request.movedTo.boardId }}
            search={{ issue: request.movedTo.issueId }}
          >
            {t('improvements.move.openIssue')}
          </Link>
        )}

        <div className="mt-2 flex flex-wrap gap-2">
          {movable && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setMoving(true);
              }}
            >
              {t('improvements.move.open')}
            </Button>
          )}
          {decidable && !open && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setOpen(true);
              }}
            >
              {t('improvements.decide.heading')}
            </Button>
          )}
          {canEdit && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditing(true);
              }}
            >
              {t('improvements.edit.open')}
            </Button>
          )}
          {selfService && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              loading={resolve.isPending}
              onClick={() => {
                resolve.mutate({ id: request.id, input: { resolution: 'withdrawn', note: '' } });
              }}
            >
              {t('improvements.withdraw.action')}
            </Button>
          )}
        </div>

        {resolve.isError && !(decidable && open) && (
          <p className="mt-2 text-sm text-[var(--color-error)]">{resolve.error.message}</p>
        )}

        {decidable && open && (
          <div className="mt-3 space-y-2 border-t border-[var(--color-border)] pt-3">
            <label
              className="text-xs text-[var(--color-text-muted)]"
              htmlFor={`improvement-note-${request.id}`}
            >
              {t('improvements.decide.note')}
            </label>
            <Textarea
              id={`improvement-note-${request.id}`}
              rows={2}
              placeholder={t('improvements.decide.notePlaceholder')}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />
            {resolve.isError && (
              <p className="text-sm text-[var(--color-error)]">{resolve.error.message}</p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                loading={resolve.isPending}
                onClick={() => {
                  decide('accepted');
                }}
              >
                {t('improvements.decide.accept')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={resolve.isPending}
                onClick={() => {
                  decide('declined');
                }}
              >
                {t('improvements.decide.decline')}
              </Button>
              {request.status === 'rejected' && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  loading={resolve.isPending}
                  onClick={() => {
                    decide('open');
                  }}
                >
                  {t('improvements.decide.reopen')}
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>

      {movable && (
        <MoveToIssueModal
          request={request}
          open={moving}
          onClose={() => {
            setMoving(false);
          }}
        />
      )}

      <Modal
        open={editing}
        onClose={() => {
          setEditing(false);
        }}
        title={t('improvements.edit.heading')}
        description={t('improvements.edit.description')}
        size="lg"
      >
        <ImprovementForm
          editing={request}
          onDone={() => {
            setEditing(false);
          }}
        />
      </Modal>
    </Card>
  );
}
